import { Injectable, signal } from '@angular/core';

import { FlatTreeNode, TreeNode, TTTreeDragOrigin } from '@treetop/shared/model';

export enum TreeDragDropInstructionType {
  ReorderAbove = 'reorder-above',
  ReorderBelow = 'reorder-below',
  MakeChild = 'make-child',
  Blocked = 'blocked',
}

export type TreeDragDropInstruction = {
  type: TreeDragDropInstructionType;
  currentLevel: number;
  indentPerLevel: number;
};

@Injectable()
export class TreeDragDropService<T extends TreeNode<unknown>> {
  public instruction = signal<TreeDragDropInstruction | null>(null);
  public dragNode = signal<FlatTreeNode<T> | null>(null);
  public targetNode = signal<FlatTreeNode<T> | null>(null);
  public dropAllowed = signal<boolean>(true);
  public dragCounter = signal<number>(0);

  private blockedNodeIds: string[] = [];

  public onDragStart(node: FlatTreeNode<T>, blockedNodes: string[]): void {
    this.dragNode.set(node);
    this.blockedNodeIds = blockedNodes;
  }

  public onDragEnter(): void {
    this.dragCounter.update((counter) => counter + 1);
  }

  public onDragLeave(): boolean {
    this.dragCounter.update((counter) => counter - 1);

    if (this.dragCounter() === 0) {
      this.targetNode.set(null);
      return true;
    }

    return false;
  }

  public onDragEnd(): void {
    this.dragNode.set(null);
    this.targetNode.set(null);
    this.instruction.set(null);
    this.dropAllowed.set(true);
    this.dragCounter.set(0);
  }

  public onDragOver(event: DragEvent, node: FlatTreeNode<T>, dragOrigin: TTTreeDragOrigin): { dropAllowed: boolean } {
    const instruction = this.getInstruction(
      { x: event.clientX, y: event.clientY },
      (event.currentTarget as HTMLElement).getBoundingClientRect(),
      node.level,
    );

    const isDragBlocked = this.blockedNodeIds.includes(node.node.id);

    if (isDragBlocked) {
      instruction.type = TreeDragDropInstructionType.Blocked;
    } else if (dragOrigin === 'tt-tree-droppable') {
      instruction.type = TreeDragDropInstructionType.MakeChild;
    }

    this.instruction.set(instruction);
    this.targetNode.set(node);

    return {
      dropAllowed: instruction.type !== TreeDragDropInstructionType.Blocked,
    };
  }

  private getInstruction(client: { x: number; y: number }, borderBox: DOMRect, currentLevel: number): TreeDragDropInstruction {
    const quarterOfHeight = borderBox.height / 4;
    let type: TreeDragDropInstructionType;

    if (client.y <= borderBox.top + quarterOfHeight) {
      type = TreeDragDropInstructionType.ReorderAbove;
    } else if (client.y >= borderBox.bottom - quarterOfHeight) {
      type = TreeDragDropInstructionType.ReorderBelow;
    } else {
      type = TreeDragDropInstructionType.MakeChild;
    }

    return { type, indentPerLevel: 20, currentLevel };
  }
}
